import { Link } from "react-router-dom";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

export default function AccessibilityStatement() {
  const { t } = useLang();
  usePageMeta(t("meta.accessibility")[0], t("meta.accessibility")[1]);
  const sections = t("statement.sections");

  return (
    <>
      <PageHero
        overline={t("statement.overline")}
        title={t("statement.title")}
        sub={t("statement.updated")}
        testId="accessibility"
      />
      <section className="bg-white py-16 sm:py-24" data-testid="accessibility-statement-content">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {sections.map((section, i) => (
            <Reveal key={section.h} delay={0} className={i > 0 ? "mt-12" : ""}>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F4C5C]">{section.h}</h2>
              {section.p?.map((para) => (
                <p key={para.slice(0, 40)} className="mt-4 text-base leading-relaxed text-slate-600">
                  {para}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2.5">
                  {section.list.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-base text-slate-700">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#D97706]" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {section.p2?.map((para) => (
                <p key={para.slice(0, 40)} className="mt-4 text-base leading-relaxed text-slate-600">
                  {para}
                </p>
              ))}
            </Reveal>
          ))}

          <Reveal className="mt-14">
            <div className="bg-[#0F4C5C] p-8 text-white" data-testid="accessibility-contact-card">
              <h2 className="font-heading text-xl font-bold">{t("statement.contactTitle")}</h2>
              <p className="mt-2 text-sm text-slate-300">{t("statement.contactSub")}</p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm">
                <a href={SITE.phone1Href} className="flex items-center gap-2 font-semibold hover:text-[#E8A34C] transition-colors duration-200" data-testid="accessibility-phone-1">
                  <Phone className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.phone1}
                </a>
                <a href={SITE.phone2Href} className="flex items-center gap-2 font-semibold hover:text-[#E8A34C] transition-colors duration-200" data-testid="accessibility-phone-2">
                  <Phone className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.phone2}
                </a>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-[#E8A34C] transition-colors duration-200" data-testid="accessibility-email">
                  <Mail className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.email}
                </a>
                <Link to="/quote" className="flex items-center gap-2 font-semibold text-[#E8A34C] hover:text-white transition-colors duration-200" data-testid="accessibility-contact-link">
                  {t("common.requestQuote")}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
